/* eslint-disable sort-exports/sort-exports */
import { z } from "zod";
import { atom } from "jotai";

import { JotaiTool } from "../../index";

interface SearchTemplatesResult {
  docs: any[];
  totalDocs: number;
}

export const SearchTemplatesToolParams = z.object({
  query: z
    .string()
    .describe(
      "The search query to find similar workflow templates and patterns",
    ),
  displayMessage: z
    .string()
    .optional()
    .describe(
      "Short 3-5 word friendly message describing what you're doing, like 'Finding workflow templates...' or 'Looking for patterns...'",
    ),
});

export type SearchTemplatesToolParamsType = z.infer<
  typeof SearchTemplatesToolParams
>;

async function searchSimilarTemplates(
  query: string,
): Promise<SearchTemplatesResult[]> {
  try {
    const response = await fetch("/api/canvasui/search-templates", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        query: query,
      }),
    });

    if (!response.ok) {
      throw new Error(
        `Search templates request failed: ${response.statusText}`,
      );
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Search templates request failed");
    }

    return data.results;
  } catch (error) {
    console.error("Error searching similar templates:", error);
    throw error;
  }
}

export const searchTemplatesToolDescription = `Searches for workflow templates and implementation patterns that can help achieve the user's goal.
- query: The search query to find similar workflow templates and patterns - use before creating complex workflows.`;

export const searchTemplatesToolAtom = atom<
  JotaiTool<z.infer<typeof SearchTemplatesToolParams>>
>(() => ({
  name: "search_templates_tool",
  description: searchTemplatesToolDescription,
  parameters: SearchTemplatesToolParams,
  execute: atom(null, async (_get, _set, { args: rawArgs }) => {
    let params: SearchTemplatesToolParamsType;
    try {
      params = SearchTemplatesToolParams.parse(rawArgs || {});
    } catch (error) {
      console.error(
        "[SEARCH_TEMPLATES_TOOL] Error parsing arguments for searchTemplatesTool:",
        error,
      );
      return {
        $error: {
          message: `Error: Invalid arguments provided for search_templates_tool. ${error instanceof Error ? error.message : "Unknown parsing error."}`,
        },
      };
    }

    const { query } = params;

    try {
      const results = await searchSimilarTemplates(query);
      return {
        content: [
          {
            type: "text",
            // Return the results, formatted as pretty JSON
            text: JSON.stringify(results, null, 2),
          },
        ],
      };
    } catch (error) {
      console.error(
        "[SEARCH_TEMPLATES_TOOL] Error in searchTemplatesToolExecute:",
        error,
      );
      return {
        $error: {
          message: `Error searching similar templates: ${error instanceof Error ? error.message : String(error)}`,
        },
      };
    }
  }),
}));

searchTemplatesToolAtom.debugLabel = "searchTemplatesToolAtom";

/* eslint-enable sort-exports/sort-exports */
